// useTableQuery / useComposedQuery — the data-layer reads for everything that
// ISN'T a single jsonb RPC (those use useRpcQuery).
//
//   • useTableQuery   — wrap ONE Supabase PostgREST builder (.from().select()…).
//                       Unwraps { data, error }, throws on error.
//   • useComposedQuery — escape hatch for MULTI-SOURCE reads: the queryFn runs
//                       arbitrary async over the browser client (Promise.all of
//                       several table selects + RPCs, sequential pipelines, etc.)
//                       and returns the composite. The caller owns its own error
//                       semantics inside the fn (e.g. selective suppression).
//
// Both share the same cache/refresh behavior as useRpcQuery (see queryClient):
// stale-while-revalidate, refetch on return-to-screen + window focus, optional
// interval polling. Mutations/realtime should call the returned refetch (or
// queryClient.invalidateQueries) after they change server state.

import { useQuery, type QueryKey, type UseQueryResult } from "@tanstack/react-query";
import { getBrowserClient } from "../supabase/browser.ts";

type BrowserClient = ReturnType<typeof getBrowserClient>;

interface PostgrestResult<T> {
  data: T | null;
  error: { message: string } | null;
}

export interface TableQueryOptions<T, S = T> {
  staleTime?: number;
  refetchInterval?: number;
  enabled?: boolean;
  select?: (raw: T) => S;
}

/**
 * Wrap a single PostgREST builder in a cached query.
 * `build` receives the browser client and returns the builder (a thenable that
 * resolves to { data, error }). Throws on error so error/empty stay distinct.
 *
 *   useTableQuery(["byo-legs", userId], (c) =>
 *     c.from("user_super_acca_byo_legs").select("*").eq("user_id", userId),
 *     { enabled: Boolean(userId) })
 */
export function useTableQuery<T, S = T>(
  key: QueryKey,
  build: (client: BrowserClient) => PromiseLike<PostgrestResult<T>>,
  opts: TableQueryOptions<T, S> = {},
): UseQueryResult<S> {
  const { staleTime, refetchInterval, enabled, select } = opts;
  return useQuery<T, Error, S>({
    queryKey: key,
    queryFn: async () => {
      const { data, error } = await build(getBrowserClient());
      if (error) throw new Error(error.message);
      return (data ?? null) as T;
    },
    staleTime,
    refetchInterval,
    enabled,
    select,
  });
}

/**
 * Escape hatch for multi-source reads. `fn` runs arbitrary async over the client
 * and returns the composite payload; it owns its own error handling (so callers
 * keep behaviors like aggregated errors or selective suppression).
 *
 *   useComposedQuery(["bankroll", userId], async (c) => {
 *     const [a, b, rec] = await Promise.all([...]);
 *     return { ...a, ...b, recommendation: rec.error ? null : rec.data };
 *   }, { enabled: Boolean(userId) })
 */
export function useComposedQuery<T, S = T>(
  key: QueryKey,
  fn: (client: BrowserClient) => Promise<T>,
  opts: TableQueryOptions<T, S> = {},
): UseQueryResult<S> {
  const { staleTime, refetchInterval, enabled, select } = opts;
  return useQuery<T, Error, S>({
    queryKey: key,
    queryFn: () => fn(getBrowserClient()),
    staleTime,
    refetchInterval,
    enabled,
    select,
  });
}
